<template>
  <div class="approved-consum-batches">
    <form class="batch-filters" @submit.prevent="applyFilters">
      <input
        v-model="search"
        type="search"
        class="form-control"
        :aria-label="$t('tools.erp_integration.consum_batch_search')"
        :placeholder="$t('tools.erp_integration.consum_batch_search')"
        :disabled="reloading"
      />
      <select v-model="status" class="form-control" :disabled="reloading" :aria-label="$t('common.status')" @change="applyFilters">
        <option value="">{{ $t("common.all") }}</option>
        <option v-for="item in statuses" :key="item" :value="item">{{ item }}</option>
      </select>
      <button type="submit" class="btn btn-sm btn-outline-primary" :disabled="reloading">
        {{ $t("common.search") }}
      </button>
      <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="reloading" @click="reload">
        {{ $t("common.refresh") }}
      </button>
    </form>
    <p class="text-muted">{{ $t("tools.erp_integration.consum_batch_help") }}</p>
    <DxDataGrid
      ref="grid"
      class="smagria-grid erp-consum-grid"
      :data-source="dataSource"
      :remote-operations="remoteOperations"
      :show-column-lines="true"
      :show-row-lines="false"
      :show-borders="true"
      :row-alternation-enabled="true"
      :column-auto-width="false"
      :allow-column-resizing="true"
      :word-wrap-enabled="true"
      :no-data-text="$t('common.no_data_text')"
      style="min-height: 320px"
      @row-expanding="onRowExpanding"
    >
      <DxPaging :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="[15, 50, 100]"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
        :info-text="$t('common.page_info')"
      />
      <DxMasterDetail :enabled="true" template="batchDetail" />
      <DxColumn data-field="uuid" :caption="$t('tools.erp_integration.consum_erp_batch')" cell-template="batchId" :allow-sorting="false" />
      <DxColumn data-field="status" :caption="$t('common.status')" cell-template="batchStatus" :allow-sorting="false" />
      <DxColumn data-field="item_count" :caption="$t('tools.erp_integration.consum_erp_summary_items_count')" :allow-sorting="false" />
      <DxColumn data-field="created_at" :caption="$t('common.created_at')" data-type="datetime" format="yyyy-MM-dd HH:mm" width="170" css-class="batch-date-column" :allow-sorting="false" />
      <DxColumn data-field="sent_at" :caption="$t('tools.erp_integration.consum_erp_sent_at')" data-type="datetime" format="yyyy-MM-dd HH:mm" width="170" css-class="batch-date-column" :allow-sorting="false" />
      <DxColumn data-field="attempt_count" :caption="$t('tools.erp_integration.reception_attempts')" :allow-sorting="false" />
      <DxColumn data-field="wm_document_number" :caption="$t('tools.erp_integration.consum_wm_document')" :allow-sorting="false" />
      <DxColumn data-field="last_error_message" :caption="$t('tools.erp_integration.reception_last_error')" :allow-sorting="false" />
      <DxColumn :caption="$t('tools.erp_integration.consum_batch_actions')" cell-template="batchActions" :allow-sorting="false" :min-width="110" />
      <template #batchId="{ data }">
        <span :title="data.data.uuid">{{ data.data.uuid.slice(0, 8) }}…</span>
      </template>
      <template #batchStatus="{ data }">
        <span class="batch-status" :class="statusClass(data.data.status)">{{ data.data.status }}</span>
      </template>
      <template #batchActions="{ data }">
        <div class="batch-actions batch-actions--compact">
          <button
            type="button"
            class="btn btn-sm btn-outline-primary batch-icon-button"
            :title="$t('tools.erp_integration.consum_view_audit')"
            :aria-label="$t('tools.erp_integration.consum_view_audit')"
            @click="$emit('open-audit', { uuid: data.data.uuid })"
          >
            <i class="fa fa-history" aria-hidden="true"></i>
          </button>
          <button
            v-if="data.data.retryable"
            type="button"
            class="btn btn-sm btn-outline-secondary batch-icon-button"
            :disabled="!!retrying[data.data.uuid]"
            :title="$t('tools.erp_integration.consum_batch_retry')"
            :aria-label="$t('tools.erp_integration.consum_batch_retry')"
            @click="retryBatch(data.data)"
          >
            <i class="fa fa-redo" aria-hidden="true"></i>
          </button>
        </div>
      </template>
      <template #batchDetail="{ data }">
        <div class="batch-detail">
          <div class="batch-actions mb-2">
            <strong class="batch-uuid">{{ data.data.uuid }}</strong>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="copyUuid(data.data.uuid)">
              {{ $t("common.copy") }}
            </button>
          </div>
          <div v-if="detailsLoading[data.data.uuid]">{{ $t("common.please_wait") }}</div>
          <div v-else-if="detailsErrors[data.data.uuid]" class="alert alert-danger">
            {{ detailsErrors[data.data.uuid] }}
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="loadDetail(data.data.uuid)">
              {{ $t("common.refresh") }}
            </button>
          </div>
          <template v-else-if="details[data.data.uuid]">
            <p>{{ $t("tools.erp_integration.consum_batch_members", { count: data.data.item_count ?? data.data.consum_count }) }}</p>
            <DxDataGrid
              class="smagria-grid erp-consum-grid"
              :data-source="details[data.data.uuid].data"
              key-expr="uuid"
              :height="420"
              :show-column-lines="true"
              :show-row-lines="false"
              :show-borders="true"
              :column-auto-width="false"
              :row-alternation-enabled="true"
              :allow-column-resizing="true"
              :no-data-text="$t('common.no_data_text')"
            >
              <DxPaging :enabled="false" />
              <DxScrolling mode="standard" show-scrollbar="always" :use-native="true" />
              <DxColumn data-field="consum_date" :caption="$t('tools.erp_integration.consum_date')" data-type="date" format="yyyy-MM-dd" />
              <DxColumn data-field="product_name" :caption="$t('common.product')" />
              <DxColumn data-field="quantity" :caption="$t('common.quantity')" data-type="number" format="#,##0.####" />
              <DxColumn data-field="measure_unit_name" :caption="$t('common.measure_unit')" />
              <DxColumn data-field="warehouse_name" :caption="$t('common.warehouse')" />
              <DxColumn data-field="work_sheet_public_id" :caption="$t('tools.erp_integration.work_sheet')" />
              <DxColumn data-field="work_date" :caption="$t('works.work_approval.work_date')" data-type="date" format="yyyy-MM-dd" />
              <DxColumn data-field="work_name" :caption="$t('works.work_approval.work_name')" />
              <DxColumn data-field="parcel_name" :caption="$t('works.work_approval.parcel_name')" />
              <DxColumn data-field="department_name" :caption="$t('works.work_approval.unit_name')" />
              <DxColumn data-field="legal_entity_name" :caption="$t('works.work_approval.legal_entity_name')" />
              <DxColumn data-field="erp_status" :caption="$t('tools.erp_integration.consum_erp_status')" />
            </DxDataGrid>
            <ConsumSummaryExportDialog
              v-if="retryContext"
              :visible.sync="retrySummaryVisible"
              :selected-rows="retryRows"
              :retry-context="retryContext"
              @close="closeRetrySummary"
              @exported="reload"
            />
          </template>
        </div>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import DxDataGrid, { DxColumn, DxPaging, DxPager, DxMasterDetail, DxScrolling } from "devextreme-vue/data-grid";
import CustomStore from "devextreme/data/custom_store";
import notify from "devextreme/ui/notify";
import { mapActions } from "vuex";
import ConsumSummaryExportDialog from "./ConsumSummaryExportDialog.vue";

export default {
  name: "ApprovedConsumBatches",
  components: { DxDataGrid, DxColumn, DxPaging, DxPager, DxMasterDetail, DxScrolling, ConsumSummaryExportDialog },
  data() {
    return {
      search: "",
      status: "",
      filters: { search: "", status: "" },
      remoteOperations: { paging: true },
      statuses: ["NEW", "VALIDATED", "SENT", "CONFIRMED", "PARTIAL_SUCCESS", "ERROR", "RETRY_WAITING", "DEAD", "CANCELED"],
      dataSource: new CustomStore({
        key: "uuid",
        load: (options) => this.loadBatches(options),
        errorHandler: (error) => notify(error.message || this.$t("tools.erp_integration.consum_action_error"), "error", 4000),
      }),
      details: {},
      detailsLoading: {},
      detailsErrors: {},
      retrying: {},
      retrySummaryVisible: false,
      retryRows: [],
      retryContext: null,
      reloading: false,
      detailGeneration: 0,
    };
  },
  beforeDestroy() {
    this.detailGeneration++;
  },
  methods: {
    ...mapActions("erpIntegration", ["fetchApprovedConsumBatches", "fetchApprovedConsumBatch"]),
    async loadBatches(options) {
      const limit = options.take || 15;
      const response = await this.fetchApprovedConsumBatches({
        ...this.filters,
        page: Math.floor((options.skip || 0) / limit) + 1,
        limit,
      });
      if (!Array.isArray(response?.data) || !Number.isFinite(response?.pagination?.total)) {
        throw new Error(this.$t("tools.erp_integration.consum_batch_load_error"));
      }
      return { data: response.data, totalCount: response.pagination.total };
    },
    async applyFilters() {
      if (this.reloading) return;
      this.filters = { search: this.search.trim(), status: this.status };
      await this.reload(true);
    },
    async reload(resetPage = false) {
      const grid = this.$refs.grid?.instance;
      if (!grid || this.reloading) return;
      this.reloading = true;
      this.detailGeneration++;
      this.details = {};
      this.detailsLoading = {};
      this.detailsErrors = {};
      grid.collapseAll(-1);
      try {
        if (resetPage === true && grid.pageIndex() !== 0) {
          await grid.pageIndex(0);
        } else {
          await grid.refresh();
        }
      } catch (error) {
        notify(error.message || this.$t("tools.erp_integration.consum_batch_load_error"), "error", 4000);
      } finally {
        this.reloading = false;
      }
    },
    onRowExpanding(event) {
      this.loadDetail(event.key);
    },
    hasCompleteMembership(response, uuid) {
      return Array.isArray(response?.data) && response?.batch?.uuid === uuid &&
        response?.membership?.complete === true &&
        !response.data.some((row) => row.erp_member_missing === true) &&
        response.data.length === (response.batch.item_count ?? response.batch.consum_count);
    },
    async loadDetail(uuid) {
      if (this.detailsLoading[uuid] || this.details[uuid]) return;
      const generation = this.detailGeneration;
      this.$set(this.detailsLoading, uuid, true);
      this.$delete(this.detailsErrors, uuid);
      try {
        const response = await this.fetchApprovedConsumBatch(uuid);
        if (generation !== this.detailGeneration) return;
        if (!this.hasCompleteMembership(response, uuid)) {
          throw new Error(this.$t("tools.erp_integration.consum_batch_members_error"));
        }
        this.$set(this.details, uuid, response);
      } catch (error) {
        if (generation === this.detailGeneration) {
          this.$set(this.detailsErrors, uuid, error?.response?.data?.message || error.message);
        }
      } finally {
        if (generation === this.detailGeneration) this.$set(this.detailsLoading, uuid, false);
      }
    },
    async retryBatch(batch) {
      if (!batch.retryable || Object.values(this.retrying).some(Boolean) || this.retryContext) return;
      const generation = this.detailGeneration;
      this.$set(this.retrying, batch.uuid, true);
      try {
        const response = await this.fetchApprovedConsumBatch(batch.uuid);
        if (generation !== this.detailGeneration) return;
        if (!this.hasCompleteMembership(response, batch.uuid)) {
          throw new Error(this.$t("tools.erp_integration.consum_batch_members_error"));
        }
        const currentBatch = response.batch;
        if (!currentBatch.retryable) {
          throw new Error(this.$t("tools.erp_integration.consum_batch_state_changed"));
        }
        const context = response.retry_context;
        if (context?.uuid !== batch.uuid || !context.revision || !context.invoice_items || !context.locks) {
          throw new Error(this.$t("tools.erp_integration.consum_batch_state_changed"));
        }
        this.retryRows = response.data;
        this.retryContext = context;
        this.retrySummaryVisible = true;
      } catch (error) {
        if (generation === this.detailGeneration) {
          notify(error?.response?.data?.message || error.message || this.$t("tools.erp_integration.consum_action_error"), "error", 4000);
        }
      } finally {
        this.$set(this.retrying, batch.uuid, false);
      }
    },
    closeRetrySummary() {
      this.retrySummaryVisible = false;
      this.retryContext = null;
      this.retryRows = [];
    },
    async copyUuid(uuid) {
      try {
        if (!navigator.clipboard?.writeText) {
          throw new Error(this.$t("tools.erp_integration.consum_batch_copy_error"));
        }
        await navigator.clipboard.writeText(uuid);
      } catch (error) {
        notify(this.$t("tools.erp_integration.consum_batch_copy_error"), "error", 4000);
      }
    },
    statusClass(status) {
      if (status === "CONFIRMED") return "batch-status--success";
      if (["ERROR", "DEAD"].includes(status)) return "batch-status--danger";
      if (["PARTIAL_SUCCESS", "RETRY_WAITING"].includes(status)) return "batch-status--warning";
      return "";
    },
  },
};
</script>

<style scoped>
.batch-filters,
.batch-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.batch-actions--compact {
  gap: 4px;
}
.batch-icon-button {
  width: 28px;
  height: 28px;
  padding: 3px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.batch-filters {
  margin-bottom: 12px;
}
.batch-filters input {
  flex: 1;
  min-width: 200px;
}
.approved-consum-batches ::v-deep .erp-consum-grid {
  height: auto;
}
.batch-filters select {
  width: auto;
}
.approved-consum-batches ::v-deep .batch-date-column {
  white-space: nowrap;
}
.batch-detail {
  padding: 12px;
}
.batch-uuid {
  user-select: all;
  overflow-wrap: anywhere;
}
.batch-status {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 12px;
  background: #f3f4f6;
  color: #374151;
}
.batch-status--success { background: #dcfce7; color: #15803d; }
.batch-status--danger { background: #fee2e2; color: #b91c1c; }
.batch-status--warning { background: #fef3c7; color: #b45309; }
</style>
